import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Client } from '../../http';
import { RootState } from '../../app/store';

// Building editor: CRUD over the building database service qualification
// reads. The API is sequoia's (/api/v1/admin/buildings); the portal's ingress
// proxies that path there, and the sign-in cookie travels with the request.

export interface BuildingSummary {
  id: string;
  address: string;
  uniqueCode: string;
  buildingName: string;
  status: string;
  type: string;
  legacyType: string;
  parentIds: string[];
}

export interface BuildingListResult {
  items: BuildingSummary[];
  total: number;
}

// The full document is schemaless (a mongoose strict:false collection),
// so the detail view works with a loose record plus the known keys.
export type BuildingDoc = Record<string, any>;

export interface ListQuery {
  search: string;
  limit: number;
  skip: number;
}

type Status = 'idle' | 'loading' | 'failed';

export interface BuildingEditorState {
  list: BuildingListResult;
  listStatus: Status;
  statuses: string[];
  types: string[];
  current: BuildingDoc | null;
  parents: BuildingSummary[];
  children: BuildingSummary[];
  slaves: BuildingSummary[];
  master: BuildingSummary | null;
  currentStatus: Status;
  saveStatus: Status;
  error: string | null;
}

const initialState: BuildingEditorState = {
  list: { items: [], total: 0 },
  listStatus: 'idle',
  statuses: [],
  types: [],
  current: null,
  parents: [],
  children: [],
  slaves: [],
  master: null,
  currentStatus: 'idle',
  saveStatus: 'idle',
  error: null,
};

const BASE = '/api/v1/admin/buildings';

const errorMessage = (err: any): string => err?.response?.data?.error
  || err?.response?.data?.message
  || err?.message
  || 'request failed';

export const fetchBuildings = createAsyncThunk(
  'buildingEditor/fetchList',
  async ({ search, limit, skip }: ListQuery) => {
    const response = await Client.get<BuildingListResult>(BASE, {
      params: { search, limit, skip },
    });
    return response.data;
  },
);

// building status/type enums: code-derived baseline + dictionary + values
// in use in the collection, assembled server-side
export const fetchStatuses = createAsyncThunk(
  'buildingEditor/fetchStatuses',
  async () => {
    const response = await Client.get<{ statuses: string[]; types: string[] }>(`${BASE}/statuses`);
    return {
      statuses: response.data.statuses || [],
      types: response.data.types || [],
    };
  },
);

export const fetchBuilding = createAsyncThunk(
  'buildingEditor/fetchOne',
  async (id: string) => {
    const [doc, parents, children, slaves] = await Promise.all([
      Client.get<BuildingDoc>(`${BASE}/${id}`),
      Client.get<BuildingSummary[]>(`${BASE}/${id}/parents`),
      Client.get<BuildingSummary[]>(`${BASE}/${id}/children`),
      Client.get<BuildingSummary[]>(`${BASE}/${id}/slaves`),
    ]);

    // slave records carry a masterBuildingId in addressComponents (the older
    // master/slave address-link mechanism); resolve it so the UI can point
    // straight at the record SQ actually serves
    let master: BuildingSummary | null = null;
    const masterId = doc.data?.addressComponents?.masterBuildingId;
    if (masterId && masterId !== id) {
      try {
        const masterDoc = await Client.get<BuildingDoc>(`${BASE}/${masterId}`);
        master = {
          id: masterId,
          address: masterDoc.data.address || '',
          uniqueCode: masterDoc.data.uniqueCode || '',
          buildingName: masterDoc.data.buildingName || '',
          status: masterDoc.data.status || '',
          type: masterDoc.data.type || '',
          legacyType: '',
          parentIds: masterDoc.data.parentIds || [],
        };
      } catch {
        master = null;
      }
    }

    return {
      doc: doc.data,
      parents: parents.data,
      children: children.data,
      slaves: slaves.data,
      master,
    };
  },
);

export const createBuilding = createAsyncThunk<
  { id: string },
  BuildingDoc,
  { rejectValue: string }
>('buildingEditor/create', async (fields, { rejectWithValue }) => {
  try {
    const response = await Client.post<{ id: string }>(BASE, fields);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(errorMessage(err));
  }
});

export const updateBuilding = createAsyncThunk<
  BuildingDoc,
  { id: string; fields: BuildingDoc },
  { rejectValue: string }
>('buildingEditor/update', async ({ id, fields }, { rejectWithValue }) => {
  try {
    const response = await Client.patch<BuildingDoc>(`${BASE}/${id}`, fields);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(errorMessage(err));
  }
});

export const deleteBuilding = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('buildingEditor/delete', async (id, { rejectWithValue }) => {
  try {
    await Client.delete(`${BASE}/${id}`);
    return id;
  } catch (err: any) {
    return rejectWithValue(errorMessage(err));
  }
});

export const attachParent = createAsyncThunk<
  void,
  { id: string; parentId: string },
  { rejectValue: string }
>('buildingEditor/attachParent', async ({ id, parentId }, { rejectWithValue, dispatch }) => {
  try {
    await Client.post(`${BASE}/${id}/parents/${parentId}`);
    dispatch(fetchBuilding(id));
  } catch (err: any) {
    return rejectWithValue(errorMessage(err));
  }
  return undefined;
});

export const detachParent = createAsyncThunk<
  void,
  { id: string; parentId: string },
  { rejectValue: string }
>('buildingEditor/detachParent', async ({ id, parentId }, { rejectWithValue, dispatch }) => {
  try {
    await Client.delete(`${BASE}/${id}/parents/${parentId}`);
    dispatch(fetchBuilding(id));
  } catch (err: any) {
    return rejectWithValue(errorMessage(err));
  }
  return undefined;
});

// attach the current building as parent of another building (add child)
export const attachChild = createAsyncThunk<
  void,
  { id: string; childId: string },
  { rejectValue: string }
>('buildingEditor/attachChild', async ({ id, childId }, { rejectWithValue, dispatch }) => {
  try {
    await Client.post(`${BASE}/${childId}/parents/${id}`);
    dispatch(fetchBuilding(id));
  } catch (err: any) {
    return rejectWithValue(errorMessage(err));
  }
  return undefined;
});

export const detachChild = createAsyncThunk<
  void,
  { id: string; childId: string },
  { rejectValue: string }
>('buildingEditor/detachChild', async ({ id, childId }, { rejectWithValue, dispatch }) => {
  try {
    await Client.delete(`${BASE}/${childId}/parents/${id}`);
    dispatch(fetchBuilding(id));
  } catch (err: any) {
    return rejectWithValue(errorMessage(err));
  }
  return undefined;
});

// point this building's addressComponents.masterBuildingId at another record
export const setMaster = createAsyncThunk<
  void,
  { id: string; masterId: string },
  { rejectValue: string }
>('buildingEditor/setMaster', async ({ id, masterId }, { rejectWithValue, dispatch }) => {
  try {
    await Client.post(`${BASE}/${id}/master/${masterId}`);
    dispatch(fetchBuilding(id));
  } catch (err: any) {
    return rejectWithValue(errorMessage(err));
  }
  return undefined;
});

export const clearMaster = createAsyncThunk<
  void,
  { id: string },
  { rejectValue: string }
>('buildingEditor/clearMaster', async ({ id }, { rejectWithValue, dispatch }) => {
  try {
    await Client.delete(`${BASE}/${id}/master`);
    dispatch(fetchBuilding(id));
  } catch (err: any) {
    return rejectWithValue(errorMessage(err));
  }
  return undefined;
});

export const buildingEditorSlice = createSlice({
  name: 'buildingEditor',
  initialState,
  reducers: {
    clearCurrent: (state) => {
      state.current = null;
      state.parents = [];
      state.children = [];
      state.slaves = [];
      state.master = null;
      state.currentStatus = 'idle';
      state.saveStatus = 'idle';
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
      state.saveStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBuildings.pending, (state) => {
        state.listStatus = 'loading';
      })
      .addCase(fetchBuildings.fulfilled, (state, action) => {
        state.listStatus = 'idle';
        state.list = {
          items: action.payload.items || [],
          total: action.payload.total || 0,
        };
      })
      .addCase(fetchBuildings.rejected, (state) => {
        state.listStatus = 'failed';
      })
      .addCase(fetchStatuses.fulfilled, (state, action) => {
        state.statuses = action.payload.statuses;
        state.types = action.payload.types;
      })
      .addCase(fetchBuilding.pending, (state) => {
        state.currentStatus = 'loading';
      })
      .addCase(fetchBuilding.fulfilled, (state, action) => {
        state.currentStatus = 'idle';
        state.current = action.payload.doc;
        state.parents = action.payload.parents || [];
        state.children = action.payload.children || [];
        state.slaves = action.payload.slaves || [];
        state.master = action.payload.master;
      })
      .addCase(fetchBuilding.rejected, (state) => {
        state.currentStatus = 'failed';
      })
      .addCase(updateBuilding.pending, (state) => {
        state.saveStatus = 'loading';
        state.error = null;
      })
      .addCase(updateBuilding.fulfilled, (state, action) => {
        state.saveStatus = 'idle';
        state.current = action.payload;
      })
      .addCase(updateBuilding.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.error = action.payload || 'update failed';
      })
      .addCase(createBuilding.pending, (state) => {
        state.saveStatus = 'loading';
        state.error = null;
      })
      .addCase(createBuilding.fulfilled, (state) => {
        state.saveStatus = 'idle';
      })
      .addCase(createBuilding.rejected, (state, action) => {
        state.saveStatus = 'failed';
        state.error = action.payload || 'create failed';
      })
      .addCase(deleteBuilding.rejected, (state, action) => {
        state.error = action.payload || 'delete failed';
      })
      .addCase(attachParent.rejected, (state, action) => {
        state.error = action.payload || 'attach parent failed';
      })
      .addCase(detachParent.rejected, (state, action) => {
        state.error = action.payload || 'detach parent failed';
      })
      .addCase(attachChild.rejected, (state, action) => {
        state.error = action.payload || 'attach child failed';
      })
      .addCase(detachChild.rejected, (state, action) => {
        state.error = action.payload || 'detach child failed';
      })
      .addCase(setMaster.rejected, (state, action) => {
        state.error = action.payload || 'set master failed';
      })
      .addCase(clearMaster.rejected, (state, action) => {
        state.error = action.payload || 'clear master failed';
      });
  },
});

export const { clearCurrent, clearError } = buildingEditorSlice.actions;

export const selectBuildingsList = (state: RootState) => state.buildingEditor.list;
export const selectBuildingsListStatus = (
  state: RootState,
) => state.buildingEditor.listStatus;
export const selectStatuses = (state: RootState) => state.buildingEditor.statuses;
export const selectTypes = (state: RootState) => state.buildingEditor.types;
export const selectBuilding = (state: RootState) => state.buildingEditor.current;
export const selectBuildingParents = (state: RootState) => state.buildingEditor.parents;
export const selectBuildingChildren = (state: RootState) => state.buildingEditor.children;
export const selectBuildingSlaves = (state: RootState) => state.buildingEditor.slaves;
export const selectBuildingMaster = (state: RootState) => state.buildingEditor.master;
export const selectBuildingStatus = (state: RootState) => state.buildingEditor.currentStatus;
export const selectSaveStatus = (state: RootState) => state.buildingEditor.saveStatus;
export const selectError = (state: RootState) => state.buildingEditor.error;

export default buildingEditorSlice.reducer;
